import { PushActionButtonType } from 'modules/graphql/types';
import { CSSProperties } from 'react';
import { caption, colors, fonts, fontWeights, letterSpacing, shadows, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { ActionButtonPreview, OS } from '../../common/types';

const notificationBackground = `radial-gradient(circle, transparent, rgba(252, 172, 209, 0.2) 100%), radial-gradient(circle, transparent, rgba(181, 109, 198, 0.3) 100%)`;

const textShadows = {
  extraLarge: [
    `0 0 ${pxToRem(1)} rgba(0, 0, 0, 0.04)`,
    `0 0 ${pxToRem(2)} rgba(0, 0, 0, 0.06)`,
    `${pxToRem(1)} ${pxToRem(1)} ${pxToRem(7)} rgba(0, 0, 0, 0.15)`,
  ],
  medium: [
    `0 0 ${pxToRem(1)} rgba(0, 0, 0, 0.04)`,
    `0 0 ${pxToRem(2)} rgba(0, 0, 0, 0.06)`,
    `0 ${pxToRem(1)} ${pxToRem(3)} rgba(0, 0, 0, 0.15)`,
  ],
};

export const containerStyles: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
  height: `100%`,
  justifyContent: `center`,
};

export const deviceStylesCommon: CSSProperties = {
  alignItems: `center`,
  borderRadius: pxToRem(20),
  display: `flex`,
  flexDirection: `column`,
  height: pxToRem(534),
  letterSpacing: letterSpacing.pos10,
  margin: `auto`,
  overflow: `hidden`,
  width: pxToRem(286),
};

export const deviceStyles = {
  [OS.android]: {
    alignItems: `center`,
    backgroundColor: colors.purple10,
    backgroundImage: notificationBackground,
    // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
    boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} rgba(181, 109, 198, 0.2)`].join(', '),
    fontFamily: fonts.roboto,
  } as CSSProperties,
  [OS.ios]: {
    backgroundColor: colors.purple10,
    backgroundImage: notificationBackground,
    // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
    boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} rgba(181, 109, 198, 0.2)`].join(', '),
    fontFamily: fonts.sfPro,
  } as CSSProperties,
};

export const deviceStylesUnset: CSSProperties = {
  backgroundColor: colors.white,
  // note: outline is being applied here as box-shadow because Safari doesn't apply border-radius to outline
  boxShadow: [...shadows.subtle, `0 0 0 ${spacing.eighth} ${colors.grey25}`].join(', '),
  justifyContent: `center`,
  textAlign: `center`,
};

export const messageUnset: CSSProperties = {
  ...caption,
  color: colors.textSecondary,
  margin: `0 ${spacing.plusHalf}`,
};

export const rotate180: CSSProperties = {
  transform: `rotate(180deg)`,
};

// Notification Specific

export const expandedNotificationTimeWrapper: CSSProperties = {
  position: `absolute`,
  top: pxToRem(50),
};

export const notificationTimeCommon: CSSProperties = {
  color: colors.white,
  fontSize: pxToRem(70),
  letterSpacing: letterSpacing.pos10,
  lineHeight: 1,
  textAlign: `center`,
  textShadow: `${textShadows.extraLarge.join(', ')}`,
};

export const expandedNotificationTimeCommon: CSSProperties = {
  ...notificationTimeCommon,
  color: `transparent`,
  textShadow: `0 0 ${pxToRem(15)} ${colors.white}`,
};

export const notificationTimeOS = {
  [OS.android]: {
    fontWeight: fontWeights.regular,
    marginTop: pxToRem(66),
  } as CSSProperties,
  [OS.ios]: {
    fontWeight: fontWeights.light,
    marginTop: pxToRem(60),
  } as CSSProperties,
};

export const notificationDate: CSSProperties = {
  color: colors.white,
  fontSize: pxToRem(18),
  fontWeight: fontWeights.regular,
  letterSpacing: letterSpacing.pos10,
  textAlign: `center`,
  textShadow: `${textShadows.medium.join(', ')}`,
};

export const expandedNotificationDate: CSSProperties = {
  ...notificationDate,
  color: `transparent`,
  textShadow: `0 0 ${pxToRem(15)} ${colors.white}`,
};

export const notificationBubble = {
  [OS.android]: {
    backgroundColor: colors.white,
    borderRadius: pxToRem(2),
    boxSizing: `border-box`,
    color: colors.black,
    display: `flex`,
    flexDirection: `column`,
    marginTop: pxToRem(15),
    padding: `${pxToRem(20)} ${pxToRem(20)} ${pxToRem(20)} ${pxToRem(20)}`,
    width: pxToRem(280),
  } as CSSProperties,
  [OS.ios]: {
    backgroundColor: `rgba(255, 255, 255, 0.7)`,
    borderRadius: pxToRem(9),
    boxSizing: `border-box`,
    display: `flex`,
    marginTop: pxToRem(16),
    padding: `${pxToRem(8)} ${pxToRem(11)} ${pxToRem(7)} ${pxToRem(6)}`,
    width: pxToRem(278),
  } as CSSProperties,
};

export const expandedNotificationBubble = {
  [OS.android]: {
    ...notificationBubble[OS.android],
    marginTop: `${pxToRem(0)}`,
    padding: `${pxToRem(0)}`,
  } as CSSProperties,
  [OS.ios]: {
    backgroundColor: `rgba(255, 255, 255, 0.7)`,
    borderRadius: pxToRem(9),
    boxSizing: `border-box`,
    flexDirection: `row`,
    overflow: `hidden`,
  } as CSSProperties,
};

export const expandedNotificationAndroidWrapper: CSSProperties = {
  display: `flex`,
  flex: 1,
  flexDirection: `column`,
  justifyContent: `center`,
};

export const expandedNotificationBubbleWrapper: CSSProperties = {
  display: `flex`,
  flex: 1,
  flexDirection: `column`,
  alignItems: `center`,
  justifyContent: `center`,
  width: pxToRem(278),
  zIndex: 1,
};

export const expandedNotificationBubbleAndroidHeaderAndBodyWrapper: CSSProperties = {
  padding: `${pxToRem(12)} ${pxToRem(10)} ${pxToRem(13)} ${pxToRem(10)}`,
  zIndex: 1,
};

export const actionButtonText = {
  [PushActionButtonType.default]: {
    color: colors.textPrimary,
  } as CSSProperties,
  [PushActionButtonType.destructive]: {
    color: colors.red100,
  } as CSSProperties,
};

const actionButtonBase = {
  alignItems: 'center',
  backgroundColor: `rgba(255, 255, 255, 0.7)`,
  boxSizing: `border-box`,
  display: `flex`,
  height: pxToRem(54),
  marginTop: pxToRem(8),
  justifyContent: 'center',
  width: pxToRem(278),
  padding: `${pxToRem(8)} ${pxToRem(11)} ${pxToRem(7)} ${pxToRem(6)}`,
  zIndex: 1,
};

export const actionButtonWrapper = {
  display: `flex`,
  flexDirection: `column`,
  alignItems: `center`,
} as CSSProperties;

export const actionButtons = {
  // Notification type
  [ActionButtonPreview.single]: {
    ...actionButtonBase,
    borderRadius: pxToRem(9),
  } as CSSProperties,
  [ActionButtonPreview.first]: {
    ...actionButtonBase,
    borderRadius: `${pxToRem(9)} ${pxToRem(9)} 0 0`,
  } as CSSProperties,
  [ActionButtonPreview.middle]: {
    ...actionButtonBase,
  } as CSSProperties,
  [ActionButtonPreview.last]: {
    ...actionButtonBase,
    borderRadius: `0 0 ${pxToRem(9)} ${pxToRem(9)}`,
  } as CSSProperties,
  // Expanded notification type
  [ActionButtonPreview.expandedSingle]: {
    ...actionButtonBase,
    width: pxToRem(206),
    marginTop: pxToRem(2),
    borderRadius: pxToRem(9),
  } as CSSProperties,
  [ActionButtonPreview.expandedFirst]: {
    ...actionButtonBase,
    width: pxToRem(206),
    marginTop: pxToRem(16),
    borderRadius: `${pxToRem(9)} ${pxToRem(9)} 0 0`,
  } as CSSProperties,
  [ActionButtonPreview.expandedMiddle]: {
    ...actionButtonBase,
    marginTop: pxToRem(2),
    width: pxToRem(206),
  } as CSSProperties,
  [ActionButtonPreview.expandedLast]: {
    ...actionButtonBase,
    width: pxToRem(206),
    marginTop: pxToRem(2),
    borderRadius: `0 0 ${pxToRem(9)} ${pxToRem(9)}`,
  } as CSSProperties,
};

export const notificationAttachmentIos: CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  borderRadius: pxToRem(4),
};

export const expandedNotificationAttachmentIos: CSSProperties = {
  maxWidth: `100%`,
  maxHeight: `fit-content`,
  borderBottomLeftRadius: pxToRem(9),
  borderBottomRightRadius: pxToRem(9),
};

export const notificationAndroidActionButton: CSSProperties = {
  color: '#1C45D8',
  paddingRight: pxToRem(15),
};

export const notificationAndroidActionButtonGroup: CSSProperties = {
  display: 'flex',
  marginTop: pxToRem(10),
};

export const expandedNotificationAndroidActionButtonGroup: CSSProperties = {
  display: `flex`,
  padding: `${pxToRem(10)} ${pxToRem(10)} ${pxToRem(10)} ${pxToRem(10)}`,
};

export const notificationAttachmentAndroid: CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
};

export const expandedNotificationAttachmentAndroid: CSSProperties = {
  maxWidth: `100%`,
  maxHeight: `fit-content`,
};

export const expandedNotificationAttachmentWrapperAndroid: CSSProperties = {
  maxHeight: spacing.x24,
  maxWidth: pxToRem(306),
  minWidth: pxToRem(278),
  overflow: `hidden`,
};

export const expandedNotificationAttachmentWrapperIos: CSSProperties = {
  ...expandedNotificationAttachmentWrapperAndroid,
  borderBottomLeftRadius: pxToRem(9),
  borderBottomRightRadius: pxToRem(9),
};

export const notificationAttachmentWrapper: CSSProperties = {
  height: pxToRem(28),
  width: pxToRem(28),
};
export const notificationContentWrapper: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
};

export const notificationBubbleAndroidHeader: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
  width: pxToRem(225),
};

export const notificationPlaceholder: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
};

export const expandedNotificationPlaceholder: CSSProperties = {
  ...notificationPlaceholder,
  flex: 1,
  padding: `${pxToRem(8)} ${pxToRem(11)} ${pxToRem(7)} ${pxToRem(6)}`,
};

export const messengerIcon: CSSProperties = {
  marginRight: pxToRem(6),
};

export const notificationBubbleAndroidIconWrapper: CSSProperties = {
  display: `flex`,
  alignItems: `center`,
  marginLeft: pxToRem(10),
};

export const notificationBubbleAndroidHeaderTime: CSSProperties = {
  alignItems: `center`,
  display: `flex`,
  flex: `1 1 auto`,
  fontSize: pxToRem(10),
  justifyContent: `flex-start`,
};

export const notificationBubbleAndroidBody: CSSProperties = {
  display: `flex`,
  justifyContent: 'space-between',
  marginTop: pxToRem(9),
  width: pxToRem(240),
};

export const notificationBubbleAndroidBodyContent: CSSProperties = {
  display: `flex`,
  flexDirection: `column`,
  width: pxToRem(200),
};

export const notificationBubbleAndroidBodyContentSender: CSSProperties = {
  fontSize: pxToRem(11),
  fontWeight: fontWeights.medium,
  overflow: `hidden`,
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
};

export const notificationBubbleAndroidBodyContentMsg: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  fontSize: pxToRem(12),
  lineHeight: 1.4,
  marginTop: pxToRem(5),
};
export const notificationBubbleAndroidBodyContentMsgTitle: CSSProperties = {
  fontSize: pxToRem(14),
  fontWeight: fontWeights.medium,
  overflow: `hidden`,
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
};

export const notificationBubbleAndroidBodyContentMsgTxt: CSSProperties = {
  flex: `1 1 auto`,
  overflow: `hidden`,
  overflowWrap: `break-word`,
  whiteSpace: `pre-wrap`,
};

export const notificationBubbleAndroidBodyContentMsgMedia: CSSProperties = {
  flex: `0 0 auto`,
  backgroundSize: `cover`,
  borderRadius: pxToRem(2),
  height: pxToRem(28),
  marginLeft: pxToRem(5),
  width: pxToRem(28),
};

export const notificationBubbleAvatar = {
  [OS.android]: {
    flex: `0 0 ${pxToRem(44)}`,
  } as CSSProperties,
  [OS.ios]: {
    flex: `0 0 ${pxToRem(38)}`,
  } as CSSProperties,
};

export const avatarAndroid: CSSProperties = {
  alignItems: `center`,
  backgroundColor: `#3147AB`,
  borderRadius: `50%`,
  display: `flex`,
  height: pxToRem(32),
  justifyContent: `center`,
  width: pxToRem(32),
};

export const avatarIos: CSSProperties = {
  backgroundImage: `linear-gradient(#A5AAB7, #858794)`,
  borderRadius: `50%`,
  height: pxToRem(28),
  marginTop: pxToRem(6),
  position: 'relative',
  width: pxToRem(28),
};

export const avatarIosBadge: CSSProperties = {
  bottom: `0`,
  position: `absolute`,
  right: pxToRem(-3),
};

export const avatarIos24: CSSProperties = {
  backgroundImage: `linear-gradient(#A5AAB7, #858794)`,
  borderRadius: `50%`,
  height: pxToRem(34),
  width: pxToRem(34),
};

export const notificationBubbleIosContent: CSSProperties = {
  flex: `0 0 auto`,
  paddingLeft: spacing.half,
  width: pxToRem(230),
};

export const expandedNotificationBubbleIosContent: CSSProperties = {
  paddingTop: pxToRem(13),
  paddingBottom: pxToRem(13),
};

export const notificationBubbleIosContentHeader: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  width: `inherit`,
};

export const expandedNotificationBubbleIosContentHeader: CSSProperties = {
  display: `flex`,
};

export const expandedNotificationBubbleIosHeaderAndBodyWrapper: CSSProperties = {
  flexDirection: `row`,
  width: pxToRem(190),
};

export const notificationBubbleIosContentHeaderSender: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  fontWeight: fontWeights.semiBold,
  overflow: `hidden`,
  textOverflow: `ellipsis`,
  whiteSpace: `nowrap`,
};

export const expandedNotificationBubbleIosContentHeaderSender: CSSProperties = {
  ...notificationBubbleIosContentHeaderSender,
  flex: `none`,
};

export const notificationBubbleIosContentHeaderTime: CSSProperties = {
  color: colors.textSecondary,
  flex: `0 0 auto`,
  fontSize: pxToRem(10),
  marginLeft: pxToRem(5),
};

export const expandedNotificationBubbleIosContentHeaderTime: CSSProperties = {
  ...notificationBubbleIosContentHeaderTime,
  marginRight: pxToRem(14),
};

export const notificationBubbleIosContentBody: CSSProperties = {
  alignItems: `flex-start`,
  display: `flex`,
  width: pxToRem(190),
};

export const notificationBubbleIosContentBodyMsg: CSSProperties = {
  color: colors.textPrimary,
  flex: `1 1 auto`,
  fontSize: pxToRem(12),
  lineHeight: 1.3,
  overflow: `hidden`,
  overflowWrap: `break-word`,
  whiteSpace: `pre-wrap`,
};

export const notificationBubbleIosContentBodyMedia: CSSProperties = {
  flex: `0 0 auto`,
  backgroundSize: `cover`,
  borderRadius: pxToRem(2),
  height: pxToRem(28),
  marginLeft: pxToRem(5),
  width: pxToRem(28),
};
